import React, { useState, useEffect, useRef, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { DillButton } from '../../../general/Dill/DillButton';
import { camelCaseToSentence } from '@dill/dill-shared/dist/src';
import moment from 'moment';
import momentTz from 'moment-timezone';
import { twMerge } from 'tailwind-merge';
import { ArrowCircleUpRight, CheckCircle, Timer, Warning } from 'phosphor-react';
import { Edit } from 'react-feather';

interface Props {
  buttonTitle: string;
  icon?: any;
  contentWidth?: number;
  buttonType?: 'PRIMARY' | 'SECONDARY' | 'FLAT' | 'TERTIARY' | 'ERROR';

  buttonStyles?: any;
  isDisabled?: boolean;
  referenceHistory: Record<string, string>[];
}

const ActionPill = ({ action }: { action?: string }) => {
  const a = action?.toLowerCase() ?? '';
  return (
    <div
      className={`w-fit px-2 py-1 rounded-full flex gap-2 text-xs flex-row items-center justify-center ${
        a === 'followup' || a === 'requested'
          ? 'WARNING_100-BG WARNING_500-CLR'
          : a === 'updated'
          ? 'PRIMARY_100-BG PRIMARY_500-CLR'
          : a === 'deferred' || a === 'bounce' || a === 'blocked'
          ? 'ERROR_100-BG ERROR_500-CLR'
          : 'SUCCESS_100-BG SUCCESS_500-CLR'
      }`}>
      {a === 'requested' && <Timer size={16} />}
      {a === 'followup' && <ArrowCircleUpRight size={16} />}
      {a === 'submitted' && <CheckCircle size={16} />}
      {a === 'updated' && <Edit size={14} />}
      {(a === 'deferred' || a === 'bounce' || a === 'blocked') && <Warning size={14} />}
      <p>{camelCaseToSentence(action ?? '')}</p>
    </div>
  );
};

export const TradeReferenceHistory: React.FC<Props> = ({
  icon,
  buttonTitle,
  contentWidth = 500,
  buttonType = 'PRIMARY',

  buttonStyles = {},
  isDisabled = false,
  referenceHistory,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);
  const currentTimeInDesiredTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const validTimezone = momentTz.tz.zone(currentTimeInDesiredTimeZone)
    ? currentTimeInDesiredTimeZone
    : 'America/Los_Angeles';

  const history = useMemo(() => {
    const normalized = referenceHistory.map((item) => {
      if ('action' in item) {
        const offsetMinutes = moment.tz(validTimezone).utcOffset();
        let date = item.date;
        if (item.date && item.actionBy === 'Dill') {
          if (offsetMinutes > 0) {
            date = moment(item.date).add(offsetMinutes, 'minutes').format();
          } else if (offsetMinutes < 0) {
            date = moment(item.date).subtract(Math.abs(offsetMinutes), 'minutes').format();
          }
        }

        return { ...item, date };
      }

      const [key, value] = Object.entries(item)[0];
      const capitalizedKey = camelCaseToSentence(key);

      return {
        [key]: value,
        actionBy: '-',
        action: key,
        note: `${capitalizedKey} on ${value}.`,
        date: value,
      };
    });

    return normalized;
  }, [referenceHistory, validTimezone]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const clickedOutsideDropdown = dropdownRef.current && !dropdownRef.current.contains(target);
      const clickedOutsideButton = buttonRef.current && !buttonRef.current.contains(target);

      if (clickedOutsideDropdown && clickedOutsideButton) {
        setIsOpen(false);
      }
    };

    let animationFrameId: number;

    const updatePosition = () => {
      if (!isOpen || !buttonRef.current) return;

      const rect = buttonRef.current.getBoundingClientRect();

      // Check if button is in viewport
      const isInViewport =
        rect.top >= 0 &&
        rect.bottom <= window.innerHeight &&
        rect.left >= 0 &&
        rect.right <= window.innerWidth;

      if (isInViewport) {
        // Get actual dropdown height if available
        const dropdownHeight = dropdownRef.current?.offsetHeight || 100;
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;

        // Determine if dropdown should open upward or downward
        const shouldOpenUpward = spaceBelow < dropdownHeight + 20 && spaceAbove > spaceBelow;

        // Update dropdown position
        setDropdownPosition({
          top: shouldOpenUpward ? rect.top - dropdownHeight - 5 : rect.bottom + 10,
          left: rect.right - contentWidth,
        });

        // Continue checking
        animationFrameId = requestAnimationFrame(updatePosition);
      } else {
        // Button is out of viewport, close dropdown
        setIsOpen(false);
      }
    };

    if (isOpen) {
      // Start position tracking
      animationFrameId = requestAnimationFrame(updatePosition);
    }

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);

      // Cancel animation frame
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [isOpen, contentWidth]);

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const columns = useMemo(() => {
    return [
      { id: 'action', name: 'Action', width: 120, cellType: 'VIEW_TEXT' },
      { id: 'date', name: 'Date', width: 100, cellType: 'VIEW_TEXT' },
      { id: 'actionBy', name: 'Action By', width: 100, cellType: 'VIEW_TEXT' },
      { id: 'notes', name: 'Notes', width: 200, cellType: 'VIEW_TEXT' },
    ];
  }, []);

  const totalWidth = useMemo(
    () =>
      columns.reduce((curr, prev) => {
        return curr + prev.width;
      }, 0),
    [columns]
  );

  return (
    <>
      <div className="relative" ref={buttonRef}>
        <DillButton
          label={buttonTitle}
          onClick={toggleDropdown}
          height={30}
          icon={icon}
          variant={
            buttonType === 'SECONDARY'
              ? 'secondary'
              : buttonType === 'FLAT'
              ? 'ghost'
              : buttonType === 'ERROR'
              ? 'danger'
              : buttonType === 'TERTIARY'
              ? 'secondary'
              : 'primary'
          }
          isDisabled={isDisabled}
          className="px-5 py-[1px]"
        />
      </div>
      {isOpen &&
        ReactDOM.createPortal(
          <div
            ref={dropdownRef}
            style={{
              position: 'fixed',
              top: `${dropdownPosition.top}px`,
              left: `${dropdownPosition.left}px`,
              width: contentWidth,
              zIndex: 9999,
            }}
            className={`border PRIMARY_50-BG py-2 rounded shadow-xl`}>
            <div className="w-full flex border-b justify-between px-4 sticky top-0 PRIMARY_50-BG items-center z-1 ">
              {columns.map((column, i) => {
                return (
                  <div
                    key={column.id + '-application-history' + i}
                    style={{ flex: column.width / totalWidth }}
                    className={twMerge('flex py-2 mx-0.5 items-center')}>
                    <p className="text-xs TEXT_SECONDARY-CLR">{column.name}</p>
                  </div>
                );
              })}
            </div>
            <div className="w-full max-h-[500px] WHITE-BG overflow-y-scroll">
              {history.map((hist, index) => {
                return (
                  <div
                    key={index + 'oo'}
                    className="w-full flex  border-b border-b-gray-200 px-4 flex-col">
                    <div className="w-full flex items-center justify-between">
                      {columns.map((column, i) => {
                        return (
                          <div
                            key={column.id + i}
                            style={{ flex: column.width / totalWidth }}
                            className={`flex py-3 mx-0.5`}>
                            {column.id === 'date' && (
                              <p className="capitalize w-full text-xs">
                                {moment(hist.date).format('MM/DD/YYYY LT')}
                              </p>
                            )}
                            {column.id === 'action' && <ActionPill action={hist.action} />}
                            {column.id === 'actionBy' && (
                              <p className="w-full text-xs">{hist.actionBy}</p>
                            )}
                            {column.id === 'notes' && <p className="w-full text-xs">{hist.note}</p>}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>,
          document.body
        )}
    </>
  );
};
